
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useQuery } from "@tanstack/react-query";
import { RecipeFormContainer } from "@/components/recipe/edit/RecipeFormContainer";
import { LoadingState } from "@/components/recipe/edit/LoadingState";
import { EditPageHeader } from "@/components/recipe/edit/EditPageHeader";
import { Ingredient, Step, CuisineType, FoodType, NutritionalValues } from "@/types/recipe";

interface DBIngredient {
  name: string;
  amount: number;
  unit: any;
}

interface DBStep {
  instruction: string;
  temperature?: string;
  time?: string;
}

const isCuisineType = (value: string | null): value is CuisineType => {
  if (!value) return false;
  const cuisineTypes: CuisineType[] = [
    'Italian', 'Lebanese', 'Greek', 'Indian', 'Chinese', 'Japanese',
    'Thai', 'Mexican', 'French', 'Spanish', 'American', 'Mediterranean',
    'Middle Eastern', 'Korean', 'Vietnamese', 'Turkish', 'Brazilian',
    'Caribbean', 'African', 'Other'
  ];
  return cuisineTypes.includes(value as CuisineType);
};

const isFoodType = (value: string | null): value is FoodType => {
  if (!value) return false;
  const foodTypes: FoodType[] = [
    'Chicken', 'Dessert', 'Snacks', 'Vegetables',
    'Ground Beef', 'Seafood', 'Soup', 'Pork', 'Other'
  ];
  return foodTypes.includes(value as FoodType);
};

const isNutritionalValues = (value: any): value is NutritionalValues => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof value.calories === 'number' &&
    typeof value.protein_g === 'number' &&
    typeof value.carbohydrates_g === 'number' &&
    typeof value.fat_g === 'number' &&
    typeof value.fiber_g === 'number' &&
    typeof value.sugar_g === 'number' &&
    typeof value.sodium_mg === 'number'
  );
};

const EditRecipe = () => {
  const { id } = useParams();

  const { data: recipe, isLoading } = useQuery({
    queryKey: ["recipe", id],
    queryFn: async () => {
      if (!id) throw new Error("No recipe ID provided");
      console.log("Fetching recipe:", id);

      const { data: recipe, error } = await supabase
        .from("recipes")
        .select("*, profiles:user_id(username)")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching recipe:", error);
        throw error;
      }
      
      if (!recipe) {
        throw new Error("Recipe not found");
      }

      const { data: normalizedData, error: normalizeError } = await supabase
        .rpc('normalize_username', { username: recipe.profiles.username });
      
      if (normalizeError) {
        console.error("Error normalizing username:", normalizeError);
        throw normalizeError;
      }

      return {
        ...recipe,
        normalizedUsername: normalizedData
      };
    },
  });

  if (isLoading || !recipe || !id) {
    return <LoadingState />;
  }

  const parsedIngredients = Array.isArray(recipe.ingredients) 
    ? (recipe.ingredients as unknown as DBIngredient[]).map(ing => ({
        name: ing.name,
        amount: Number(ing.amount),
        unit: ing.unit
      }))
    : [{ amount: 0, unit: "cup" as const, name: "" }];

  const parsedSteps = Array.isArray(recipe.steps)
    ? (recipe.steps as unknown as DBStep[]).map(step => ({
        instruction: step.instruction,
        temperature: step.temperature || undefined,
        time: step.time || undefined
      }))
    : [{ instruction: "" }];

  const validCuisineType = isCuisineType(recipe.cuisine_type) ? recipe.cuisine_type : '';
  const validFoodType = isFoodType(recipe.food_type) ? recipe.food_type : '';

  // Set default nutritional values if none exist or if the data is invalid
  const defaultNutritionalValues: NutritionalValues = {
    calories: 0,
    protein_g: 0,
    carbohydrates_g: 0,
    fat_g: 0,
    fiber_g: 0,
    sugar_g: 0,
    sodium_mg: 0
  };

  const validNutritionalValues = isNutritionalValues(recipe.nutritional_values) 
    ? recipe.nutritional_values as NutritionalValues 
    : defaultNutritionalValues;

  const initialData = {
    title: recipe.title,
    description: recipe.description || "",
    ingredients: parsedIngredients as Ingredient[],
    steps: parsedSteps as Step[],
    imageUrl: recipe.image_url,
    visibility: recipe.visibility as "public" | "subscription",
    cuisineType: validCuisineType as CuisineType | '',
    foodType: validFoodType as FoodType | '',
    servings: recipe.servings || 1,
    nutritionalValues: validNutritionalValues
  };

  return (
    <>
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <EditPageHeader normalizedUsername={recipe.normalizedUsername} id={id} />
        <RecipeFormContainer id={id} initialData={initialData} />
      </div>
      <Footer />
    </>
  );
};

export default EditRecipe;
